import React, { memo, useEffect, useRef, useState, useMemo } from "react";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";
import worldMap from "./utilities/worldMap.json";
import Pointer from "../WorldMap/assets/Pointer.svg";
import PropTypes from "prop-types";
import "./WorldMap.scss";
import ArrowUp from "../WorldMap/assets/upArrow.svg";
import ArrowDown from "../WorldMap/assets/downArrow.svg";
import ArrowLeft from "../WorldMap/assets/leftArrow.svg";
import ArrowRight from "../WorldMap/assets/rightArrow.svg" ;
import ZoomIn from "../WorldMap/assets/zoomIn.svg" ;
import ZoomOut from "../WorldMap/assets/zoomOut.svg" ;
import ResetIcon from "../WorldMap/assets/fitScreen.svg" ;
import UnlockIcon from "../WorldMap/assets/UnlockIcon.svg" ;
import LockIcon from "../WorldMap/assets/LockIcon.svg" ;


echarts.registerMap("world", worldMap);

const WorldMap = ({ openModal, dottedPatternImag, Data ,legend, MapBorderColor="transparent",TooltipFormatter }) => {
  const chartRef = useRef(null);
  const mapCenterRef = useRef([0, 20]);
  const mapZoomRef = useRef(1.2);
  const [pointerBase64, setPointerBase64] = useState(null);
  const [zoomLocked, setZoomLocked] = useState(true);

  // Load custom pointer image
  useEffect(() => {
    const loadPointerImage = async () => {
      const response = await fetch(Pointer);
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onloadend = () => setPointerBase64(reader.result);
      reader.readAsDataURL(blob);
    };
    loadPointerImage();
  }, []);

  // Handle zooming
  const handleZoom = (type) => {
    const chart = chartRef.current?.getEchartsInstance?.();
    if (!chart) {return;}

    let newZoom = mapZoomRef.current;
    let newCenter = [...mapCenterRef.current];

    if (type === "in") {newZoom *= 1.2;}
    else if (type === "out") {newZoom /= 1.2;}
    else if (type === "reset") {
      newZoom = 1.2;
      newCenter = [0, 20];
    }

    mapZoomRef.current = newZoom;
    mapCenterRef.current = newCenter;

    chart.setOption({
      geo: [{
        ...chart.getOption().geo[0],
        zoom: newZoom,
        center: newCenter,
      }],
    }, false); // notMerge: false
  };

  // Region click
  const handleClick = (params) => {
    const clicked = Data.find((item) => item.name === params.name);
    if (clicked) {openModal?.(clicked);}
  };

  const toggleZoomLock = () => {
  setZoomLocked((prev) => !prev);

  const chart = chartRef.current?.getEchartsInstance?.();
  if (!chart) return;

  const currentGeo = chart.getOption().geo?.[0];
  if (!currentGeo) return;

  chart.setOption({
    geo: [{
      ...currentGeo,
      roam: !zoomLocked ? false : true, // toggle roam
    }],
  }, false);
};


  // Track zoom/center on roam
  useEffect(() => {
    const chart = chartRef.current?.getEchartsInstance?.();
    if (!chart) {return;}

    const handleRoam = () => {
      const geo = chart.getModel().getComponent("geo");
      const center = geo.option.center;
      const zoom = geo.option.zoom;
      mapCenterRef.current = [...center];
      mapZoomRef.current = zoom;
    };

    chart.on("georoam", handleRoam);
    return () => chart.off("georoam", handleRoam);
  }, []);

  // Tooltip link click
  useEffect(() => {
    const onTooltipClick = (e) => {
      const azName = e.detail;
      const clickedAZ = Data.find((az) => az.name === azName);
      if (clickedAZ) {openModal?.(clickedAZ);}
    };
    window.addEventListener("tooltipClick", onTooltipClick);
    return () => window.removeEventListener("tooltipClick", onTooltipClick);
  }, [Data]);

  const option = useMemo(() => ({
    backgroundColor: "#fff",
    geo: {
      map: "world",
      roam: zoomLocked ? false : true,
      center: mapCenterRef.current,
      zoom: mapZoomRef.current,
      scaleLimit: { min: 0.5, max: 10 },
      itemStyle: {
        areaColor: dottedPatternImag ? { image: dottedPatternImag, repeat: "repeat" } : "#fff",
        borderColor: MapBorderColor,
      },
      label: { show: false },
      emphasis: { disabled: true },
    },
    tooltip: {
      show: true,
      trigger: "item",
      enterable: true,
      backgroundColor: "transparent",
      borderColor: "transparent",
      borderWidth: 0,
      padding: 0,
      extraCssText: "box-shadow: none;",
      position: (point) => [point[0] + 18, point[1] - 18],
      formatter: TooltipFormatter,
      extraCssText: "z-index: 1000 !important;",
    },
   series: [
  {
    name: "Regions",
    type: "scatter",
    coordinateSystem: "geo",
    geoIndex: 0,
    zlevel: 2,
    symbol: pointerBase64 ? `image://${pointerBase64}` : "pin",
    symbolSize: [50, 40],
    label: {
      show: true,
      formatter: (params) => {
        const regionData = Data.find((d) => d.name === params.name);
        return regionData?.regionName || "";
      },
      position: "right",
      color: "#000",
      fontSize: 12,
      fontWeight: 600,
      backgroundColor: "#fff",
      padding: [4, 6],
      borderRadius: 4,
      shadowColor: "rgba(0,0,0,0.1)",
      shadowBlur: 3,
    },
    data: Data.map((d) => ({
      name: d.name,
      value: d.coords,
    })),
  },
],

  }), [dottedPatternImag, pointerBase64,Data]);

  const chartInstance = useMemo(() => (
    <ReactECharts
      ref={chartRef}
      option={option}
      style={{ height: "100%", width: "100%" }}
      onEvents={{ click: handleClick }}
      notMerge={true}
      lazyUpdate={true}
    />
  ), [option]);

  return (
    <div>
       {legend && (
        <div style={{ marginBottom: 10 }}>{legend}</div>
      )}

      <div style={{ position: "relative", height: "500px", width: "100%" }}>
         <div className="zoom-controls">
          <button onClick={() => handleZoom("in")} className="zoom-button" title="Zoom In"><img src={ZoomIn} alt="Zoom In" /></button>
          <button onClick={() => handleZoom("out")} className="zoom-button" title="Zoom Out"><img src={ZoomOut} alt="Zoom Out" /></button>
          <button onClick={() => handleZoom("reset")} className="zoom-button" title="Reset"><img src={ResetIcon} alt="Zoom Out" /></button>
          <button onClick={toggleZoomLock} className="zoom-button" title={zoomLocked ? "Unlock Zoom" : "Lock Zoom"}>
          <img src={zoomLocked ? LockIcon : UnlockIcon} alt="Zoom Lock" className="Lock" />
          </button>
        </div> 
        {chartInstance}
      </div>
    </div>
  );
};

WorldMap.propTypes = {
  openModal: PropTypes.func.isRequired,
  dottedPatternImag: PropTypes.object,
  Data: PropTypes.array.isRequired,
  legend: PropTypes.node,
  MapBorderColor: PropTypes.string,
  TooltipFormatter: PropTypes.func,
};

export default memo(WorldMap);



